import { z } from 'zod'
import { ENTITY_TYPES, type ChatChoice, type Pending } from '@edge/edge-ai'

/**
 * Wire shapes for /v1/companion/threads (edge-companion-api thread sync).
 * Zod first per house rule — TS types derive from these.
 */

/** One button under an answer: the companion's ChatChoice, read off the wire. */
const ThreadWireChoiceSchema = z.object({
  label: z.string(),
  detail: z.string().optional(),
}) satisfies z.ZodType<ChatChoice>

/**
 * What an answer left its thread waiting on (spec 2026-10-18, 6.2): the
 * companion's `Pending` as a thread keeps it, a parked tour with its steps.
 */
const ThreadWirePendingSchema = z.object({
  kind: z.enum(['question', 'choices', 'walkthrough_offer']),
  from: z.enum(['assistant', 'analyst']),
  resolves: z.enum(ENTITY_TYPES).optional(),
  createdAt: z.number(),
  walkthrough: z
    .object({
      steps: z.array(z.object({ instruction: z.string(), target: z.string() })),
      panel: z.string().optional(),
      label: z.string(),
      slug: z.string().optional(),
    })
    .optional(),
}) satisfies z.ZodType<Pending>

/**
 * What the server keeps of an answer's choice buttons: the same numbers as
 * MAX_MESSAGE_CHOICES, MAX_CHOICE_LABEL_CHARS and MAX_CHOICE_DETAIL_CHARS in
 * edge-companion-api/src/threads/router.ts. The server cuts to them rather than
 * refusing, because a 422 would leave the whole conversation unsynced
 * (flushDirtyThreads skips a rejected thread on every flush); the client cuts
 * first, so what a thread keeps locally is what the server keeps. A drift only
 * means the server cuts more.
 */
export const MAX_MESSAGE_CHOICES = 50
export const MAX_CHOICE_LABEL_CHARS = 256
export const MAX_CHOICE_DETAIL_CHARS = 512

/** `text` cut to at most `max` UTF-16 units, never between the halves of a surrogate pair. */
function clip(text: string, max: number): string {
  if (text.length <= max) return text
  const last = text.charCodeAt(max - 1)
  return text.slice(0, last >= 0xd800 && last <= 0xdbff ? max - 1 : max)
}

/**
 * The choice buttons of one answer as they are saved with the thread: at most
 * MAX_MESSAGE_CHOICES, each label and detail cut to its cap. An entry with no
 * text label is dropped, a detail that is not text is left out, and no buttons
 * at all is `undefined`, so a message never carries an empty list.
 */
export function clampChoices(choices: readonly ChatChoice[] | undefined): ChatChoice[] | undefined {
  const kept: ChatChoice[] = []
  for (const choice of choices ?? []) {
    if (kept.length === MAX_MESSAGE_CHOICES) break
    if (typeof choice?.label !== 'string') continue
    kept.push({
      label: clip(choice.label, MAX_CHOICE_LABEL_CHARS),
      ...(typeof choice.detail === 'string'
        ? { detail: clip(choice.detail, MAX_CHOICE_DETAIL_CHARS) }
        : {}),
    })
  }
  return kept.length ? kept : undefined
}

export const ThreadWireMessageSchema = z.object({
  role: z.enum(['user', 'assistant']),
  text: z.string(),
  timestamp: z.number(),
  toolCalls: z.array(z.unknown()).optional(),
  toolResults: z.array(z.unknown()).optional(),
  /** Without this a pulled thread loses the buttons: zod drops keys it does not name. */
  choices: z.array(ThreadWireChoiceSchema).optional(),
  /**
   * What the answer left pending, so a thread opened on another device keeps
   * it. One this client cannot read (a newer kind) is dropped, never the thread.
   */
  pending: ThreadWirePendingSchema.optional().catch(undefined),
})

export const ThreadWireIndexEntrySchema = z.object({
  threadId: z.string(),
  title: z.string().nullable(),
  page: z.string().nullable(),
  contextKey: z.string().nullable(),
  messageCount: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const ThreadListResponseSchema = z.object({
  threads: z.array(ThreadWireIndexEntrySchema),
})

export const ThreadWireSchema = ThreadWireIndexEntrySchema.extend({
  messages: z.array(ThreadWireMessageSchema),
})

export const PutThreadResponseSchema = z.object({
  ok: z.boolean(),
  applied: z.boolean(),
  serverUpdatedAt: z.string().nullable(),
})

export type ThreadWireIndexEntry = z.infer<typeof ThreadWireIndexEntrySchema>
export type ThreadWire = z.infer<typeof ThreadWireSchema>
export type PutThreadResponse = z.infer<typeof PutThreadResponseSchema>
